import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { Flex, Box } from 'grid-styled';
import { Link } from 'react-router-dom';
import connect from '../store/connect';
import { fetchArticlePageView } from '../actions/actionCreators';
import Page from './Page';
import { Container } from '../styles/Container';
import { Heading } from '../styles/Heading';
import NotFoundPage from './NotFoundPage';
import ErrorPage from './ErrorPage';
import { SITE_ROOT_URL } from '../constants/endpoints';
import { SubHeading } from '../styles/SubHeading';
import { color } from '../styles/helpers/variables';
import { HtmlWrapper } from '../styles/HtmlWrapper';
import RenderPrice from '../components/RenderPrice';
import { fontFamilyBody } from '../styles/helpers/mixins';
import { Conditional } from '../components/Conditional';
import { Section } from '../styles/Section';
import StoreItemCard from '../components/StoreItemCard';
import LinedHeading from '../components/LinedHeading';
import { LoadingHeading, LoadingPreamble, LoadingBody } from '../styles/LoadingState';

class StoreItemPage extends Component {
  componentDidMount() {
    this.props.fetchArticlePageView({ urlPath: this.props.location.pathname, hasLinkedContent: true });
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.props.fetchArticlePageView({ urlPath: this.props.location.pathname, hasLinkedContent: true });
    }
  }

  render() {
    const { currentPage, request } = this.props;

    const imageProps = '?width=1600';
    const responsiveGridArrayFour = [1 / 2, 1 / 2, 1 / 3, 1 / 4];
    const gridPadding = ['5px', '7px', '10px'];
    const negativeGridPadding = ['-5px', '-7px', '-10px'];

    if (request.get('statusCode') === 404) return <NotFoundPage />;
    if (request.get('status') === 'ERROR') return <ErrorPage />;
    if (request.get('status') === 'LOADING')
      return (
        <Page meta={currentPage.get('metaData')} bgColor="#f2f2f2">
          <Container>
            <LoadingHeading />
            <LoadingPreamble />
            <LoadingBody />
          </Container>
        </Page>
      );

    return (
      <Page meta={currentPage.get('metaData')} bgColor="#f2f2f2">
        <Container>
          <Flex flexWrap="wrap">
            <Box width={[1, 7 / 12, 5 / 12]} py={[0, 32, 32]} pr={[0, 40, 80]}>
              <Conditional show={!!currentPage.getIn(['fields', 'brand', 'url'])}>
                <SubHeading.link
                  to={currentPage.getIn(['fields', 'brand', 'url'])}
                  color={color.black}
                  margin="0"
                  uppercase
                >
                  {currentPage.getIn(['fields', 'brand', 'name'])}
                </SubHeading.link>
              </Conditional>

              <Heading margin="0" style={{ lineHeight: 1.2 }}>
                {currentPage.getIn(['fields', 'productName'])}
              </Heading>

              <RenderPrice
                price={Number(currentPage.getIn(['fields', 'productPrice']))}
                originalPrice={Number(currentPage.getIn(['fields', 'productOriginalPrice']))}
              />

              <HtmlWrapper dangerouslySetInnerHTML={{ __html: currentPage.getIn(['fields', 'description']) }} />

              <Conditional show={!!currentPage.getIn(['fields', 'originalProduct'])}>
                <LinkToProduct to={currentPage.getIn(['fields', 'originalProduct'])}>
                  Läs mer om produkten
                </LinkToProduct>
              </Conditional>

              <ContactStoreTitle>Kontakta butiken:</ContactStoreTitle>
              <Conditional show={!!currentPage.getIn(['fields', 'emailAddress'])}>
                <ContactStoreLink href={`mailto:${currentPage.getIn(['fields', 'emailAddress'])}`}>
                  {currentPage.getIn(['fields', 'emailAddress'])}
                </ContactStoreLink>
              </Conditional>
              <Conditional show={!!currentPage.getIn(['fields', 'phoneNumber'])}>
                <ContactStoreLink href={`tel:${currentPage.getIn(['fields', 'phoneNumber'])}`}>
                  {currentPage.getIn(['fields', 'phoneNumber'])}
                </ContactStoreLink>
              </Conditional>
            </Box>

            <Box width={[1, 5 / 12, 7 / 12]} py={[20, 32, 32]}>
              <Conditional show={!!currentPage.getIn(['fields', 'productImage', 'url'])}>
                <ProductImage
                  src={SITE_ROOT_URL + currentPage.getIn(['fields', 'productImage', 'url']) + imageProps}
                  alt={currentPage.getIn(['fields', 'productImage', 'alt'])}
                />
              </Conditional>
            </Box>
          </Flex>
        </Container>

        <Conditional show={!!currentPage.get('linkedContent')}>
          <Container>
            <Section pb={80}>
              <LinedHeading bgColor="#f2f2f2">Fler uställningsexemplar</LinedHeading>
              <Flex flexWrap="wrap" mx={negativeGridPadding}>
                {currentPage.get('linkedContent') &&
                  currentPage.get('linkedContent').map(child => (
                    <Box key={child.get('url')} width={responsiveGridArrayFour} px={gridPadding} my={gridPadding}>
                      <StoreItemCard
                        url={child.get('url')}
                        productName={child.getIn(['fields', 'listTitle']) || child.getIn(['fields', 'productName'])}
                        brand={child.getIn(['fields', 'brand'])}
                        image={child.getIn(['fields', 'listImage']) || child.getIn(['fields', 'productImage'])}
                        tag={child.getIn(['fields', 'tag'])}
                        price={Number(child.getIn(['fields', 'productPrice']))}
                        originalPrice={Number(child.getIn(['fields', 'productOriginalPrice']))}
                      />
                    </Box>
                  ))}
              </Flex>
            </Section>
          </Container>
        </Conditional>
      </Page>
    );
  }
}

StoreItemPage.defaultProps = {
  currentPage: Map(),
  request: Map()
};

StoreItemPage.propTypes = {
  fetchArticlePageView: PropTypes.func.isRequired,
  currentPage: PropTypes.instanceOf(Map),
  request: PropTypes.instanceOf(Map),
  location: PropTypes.object.isRequired
};

export default connect(
  StoreItemPage,
  { fetchArticlePageView },
  (store, props) => ({
    currentPage: store.page.data.get(props.location.pathname),
    request: store.page.request.get(props.location.pathname)
  })
);

const ProductImage = styled.img`
  width: 100%;
`;

const LinkToProduct = styled(Link)`
  color: black;
`;

const ContactStoreTitle = styled.h3`
  ${fontFamilyBody};
  font-size: 14px;
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.57;
  letter-spacing: normal;
  color: ${color.black};
  margin-bottom: 5px;
`;

const ContactStoreLink = styled.a`
  ${fontFamilyBody};
  font-size: 16px;
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.69;
  letter-spacing: normal;
  color: ${color.primary};
  margin: 3px 0;
  display: block;
  text-decoration: none;
  transition: opacity 0.25s ease-out;

  &:hover {
    opacity: 0.8;
  }
`;



// WEBPACK FOOTER //
// ./src/pages/StoreItemPage.js